/**
 * Opportunity Service for PBS
 *
 * Opportunities are prospective work modelled in What-If before it exists in
 * Redmine. They are kept separate from projects: their own ids, their own
 * endpoints, and their own saved scenarios (never saved project dashboards).
 */

import apiClient from './apiClient';
import { Opportunity } from '../types/api';
import { DashboardData } from './dashboardService';

export interface CreateOpportunityInput {
  name: string;
  description?: string;
}

/** Summary of a saved What-If scenario for an opportunity. */
export interface OpportunityScenarioSummary {
  id: number;
  opportunityId: number;
  opportunityName: string | null;
  /** The opportunity was deleted after this scenario was saved. */
  opportunityDeleted: boolean;
  label: string;
  calculatedCosts: DashboardData['calculatedCosts'];
  memberCount: number;
  costCount: number;
  savedAt: string;
  ownerId: number | null;
}

/** A full saved scenario - the same figures a saved project dashboard holds. */
export interface OpportunityScenario extends OpportunityScenarioSummary {
  allocationMembers: DashboardData['allocationMembers'];
  projectCosts: DashboardData['projectCosts'];
  monthlyBreakdown: DashboardData['monthlyBreakdown'];
}

/** What the What-If page sends when saving a scenario. */
export type OpportunityScenarioInput = Pick<
  DashboardData,
  'label' | 'allocationMembers' | 'projectCosts' | 'calculatedCosts' | 'monthlyBreakdown' | 'savedAt'
>;

export interface Pagination {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

/** One page of scenario summaries. */
export interface ScenarioPage {
  scenarios: OpportunityScenarioSummary[];
  pagination: Pagination;
}

interface Envelope<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * The axios interceptor rejects with a flattened ApiError that has no `.response`,
 * so error text must be read off `.message` rather than `err.response.data.error`.
 */
const errorMessage = (error: any, fallback: string): string =>
  (error && typeof error.message === 'string' && error.message) || fallback;

class OpportunityService {
  async createOpportunity(input: CreateOpportunityInput): Promise<Opportunity> {
    try {
      const response = await apiClient.post<Envelope<Opportunity>>('/opportunities', input);
      return response.data.data;
    } catch (error) {
      throw new Error(errorMessage(error, 'Failed to create opportunity'));
    }
  }

  async listOpportunities(): Promise<Opportunity[]> {
    try {
      const response = await apiClient.get<Envelope<Opportunity[]>>('/opportunities');
      return response.data.data || [];
    } catch (error) {
      console.error('Failed to list opportunities:', error);
      return [];
    }
  }

  async getOpportunity(id: number): Promise<Opportunity> {
    try {
      const response = await apiClient.get<Envelope<Opportunity>>(`/opportunities/${id}`);
      return response.data.data;
    } catch (error) {
      throw new Error(errorMessage(error, 'Failed to load opportunity'));
    }
  }

  async updateOpportunity(id: number, input: CreateOpportunityInput): Promise<Opportunity> {
    try {
      const response = await apiClient.put<Envelope<Opportunity>>(`/opportunities/${id}`, input);
      return response.data.data;
    } catch (error) {
      throw new Error(errorMessage(error, 'Failed to update opportunity'));
    }
  }

  async deleteOpportunity(id: number): Promise<void> {
    try {
      await apiClient.delete(`/opportunities/${id}`);
    } catch (error) {
      throw new Error(errorMessage(error, 'Failed to delete opportunity'));
    }
  }

  // --- Saved What-If scenarios ---------------------------------------------

  /** A page of every opportunity's saved scenarios, newest first. */
  async listAllScenarios(limit = 10, offset = 0): Promise<ScenarioPage> {
    try {
      const response = await apiClient.get<Envelope<OpportunityScenarioSummary[]> & { pagination: Pagination }>(
        '/opportunities/scenarios',
        { params: { limit, offset } }
      );
      return { scenarios: response.data.data || [], pagination: response.data.pagination };
    } catch (error) {
      throw new Error(errorMessage(error, 'Failed to load opportunity scenarios'));
    }
  }

  /** A page of one opportunity's saved scenarios, newest first. */
  async listScenarios(opportunityId: number, limit = 10, offset = 0): Promise<ScenarioPage> {
    try {
      const response = await apiClient.get<Envelope<OpportunityScenarioSummary[]> & { pagination: Pagination }>(
        `/opportunities/${opportunityId}/scenarios`,
        { params: { limit, offset } }
      );
      return { scenarios: response.data.data || [], pagination: response.data.pagination };
    } catch (error) {
      throw new Error(errorMessage(error, 'Failed to load scenarios'));
    }
  }

  async getScenario(scenarioId: number): Promise<OpportunityScenario> {
    try {
      const response = await apiClient.get<Envelope<OpportunityScenario>>(`/opportunities/scenarios/${scenarioId}`);
      return response.data.data;
    } catch (error) {
      throw new Error(errorMessage(error, 'Failed to load scenario'));
    }
  }

  async saveScenario(opportunityId: number, input: OpportunityScenarioInput): Promise<OpportunityScenario> {
    try {
      const response = await apiClient.post<Envelope<OpportunityScenario>>(
        `/opportunities/${opportunityId}/scenarios`,
        input
      );
      return response.data.data;
    } catch (error) {
      throw new Error(errorMessage(error, 'Failed to save scenario'));
    }
  }

  async updateScenario(scenarioId: number, input: OpportunityScenarioInput): Promise<OpportunityScenario> {
    try {
      const response = await apiClient.put<Envelope<OpportunityScenario>>(
        `/opportunities/scenarios/${scenarioId}`,
        input
      );
      return response.data.data;
    } catch (error) {
      throw new Error(errorMessage(error, 'Failed to update scenario'));
    }
  }

  async deleteScenario(scenarioId: number): Promise<void> {
    try {
      await apiClient.delete(`/opportunities/scenarios/${scenarioId}`);
    } catch (error) {
      throw new Error(errorMessage(error, 'Failed to delete scenario'));
    }
  }
}

const opportunityService = new OpportunityService();
export default opportunityService;
